/**
 * Leaderboard — per-game best scores or global totals.
 *
 * Uses the existing security-definer RPCs so only opted-in profiles
 * (show_on_leaderboard != false) appear.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Trophy, Medal } from 'lucide-react';
import {
  gamePlayService,
  type LeaderboardRow,
  type GlobalLeaderboardRow,
} from '@/services/gamePlayService';

interface LeaderboardProps {
  gameId?: string;
  defaultTab?: 'game' | 'global';
}

export const Leaderboard = ({ gameId, defaultTab = 'game' }: LeaderboardProps) => {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Trophy className="h-5 w-5 text-primary" /> Leaderboard
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={gameId ? defaultTab : 'global'}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="game" disabled={!gameId}>
              This game
            </TabsTrigger>
            <TabsTrigger value="global">Global</TabsTrigger>
          </TabsList>
          {gameId && (
            <TabsContent value="game" className="mt-4">
              <GameRows gameId={gameId} />
            </TabsContent>
          )}
          <TabsContent value="global" className="mt-4">
            <GlobalRows />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

const RankBadge = ({ rank }: { rank: number }) => {
  if (rank === 1) return <Medal className="h-4 w-4 text-yellow-500" />;
  if (rank === 2) return <Medal className="h-4 w-4 text-muted-foreground" />;
  if (rank === 3) return <Medal className="h-4 w-4 text-amber-700" />;
  return <span className="text-sm text-muted-foreground w-4 text-center">{rank}</span>;
};

const GameRows = ({ gameId }: { gameId: string }) => {
  const [rows, setRows] = useState<LeaderboardRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    gamePlayService
      .getGameLeaderboard(gameId)
      .then(setRows)
      .catch((e) => setError(e.message ?? 'Failed to load leaderboard'));
  }, [gameId]);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!rows) return <RowSkeleton />;
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        Be the first to set a high score!
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {rows.map((r, i) => (
        <li
          key={r.user_id}
          className="flex items-center justify-between rounded-md border p-2"
        >
          <div className="flex items-center gap-3">
            <RankBadge rank={i + 1} />
            <span className="font-medium">{r.display_name}</span>
            <Badge variant="outline" className="text-xs">
              {r.plays} {r.plays === 1 ? 'play' : 'plays'}
            </Badge>
          </div>
          <span className="font-semibold tabular-nums">{r.best_score}</span>
        </li>
      ))}
    </ul>
  );
};

const GlobalRows = () => {
  const [rows, setRows] = useState<GlobalLeaderboardRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    gamePlayService
      .getGlobalLeaderboard()
      .then(setRows)
      .catch((e) => setError(e.message ?? 'Failed to load leaderboard'));
  }, []);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!rows) return <RowSkeleton />;
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        No leaderboard entries yet.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {rows.map((r, i) => (
        <li
          key={r.user_id}
          className="flex items-center justify-between rounded-md border p-2"
        >
          <div className="flex items-center gap-3">
            <RankBadge rank={i + 1} />
            <span className="font-medium">{r.display_name}</span>
            <Badge variant="outline" className="text-xs">
              {r.games_played} games
            </Badge>
          </div>
          <span className="font-semibold tabular-nums">{r.total_score}</span>
        </li>
      ))}
    </ul>
  );
};

const RowSkeleton = () => (
  <div className="space-y-2">
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} className="h-10 w-full" />
    ))}
  </div>
);

export default Leaderboard;
